import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { WeekFilter } from '../ui/week-filter';
import { overlapsWeek } from '../../lib/weekFilter';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AssetDetail from './AssetDetail';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Search, X, FolderOpen, Users, Package, AlertTriangle, RefreshCw, FileSpreadsheet, CheckSquare, Square, UserCheck, Undo2, QrCode } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import MatrixAssignDialog from './MatrixAssignDialog';
import FloatingActions from '../FloatingActions';
import { CopyIconButton } from '../shared/CopyableLabel';
import { exportView } from '../../lib/exportXlsx';
import { fmtDateWeek } from '../../lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLS = {
  assigned: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-amber-100 text-amber-800',
  unassigned: 'bg-slate-100 text-slate-700',
};

const Holder = ({ assignment, onNavigate }) => {
  const isProject = assignment.assignment_type === 'project';
  const Icon = isProject ? FolderOpen : Users;
  const name = assignment.target_name || assignment.target_id;
  return (
    <span className="inline-flex items-center gap-1 mr-2">
      <Icon className={`w-3.5 h-3.5 shrink-0 ${isProject ? 'text-blue-600' : 'text-violet-600'}`} />
      {onNavigate ? (
        <button className="text-blue-700 hover:underline text-left break-words"
          onClick={() => onNavigate(assignment)}
          data-testid={`holder-link-${assignment.id}`}>{name}</button>
      ) : <span className="break-words">{name}</span>}
    </span>
  );
};

/** One filterable list answering "where is each asset right now?". */
const AssignmentsOverview = ({ navigateTo, categories = [], conditions = [], suppliers = [],
  employees = [], projects = [], isAdmin = false, config = {} }) => {
  const { t, i18n } = useTranslation();
  const statusLabel = (s) => t(`inv.overview.status.${s}`, { defaultValue: s });
  const holderLabel = (row) => (row.current || []).map((a) => a.target_name || a.target_id).join(', ')
    || (row.last ? t('inv.overview.notBookedBack', { name: row.last.target_name || row.last.target_id }) : '');
  const [detailAsset, setDetailAsset] = useState(null);
  const [data, setData] = useState({ rows: [], totals: null, as_of: '' });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const [status, setStatus] = useState('all');
  const [holderType, setHolderType] = useState('all');
  const [holderId, setHolderId] = useState('all');
  // Kit usually moves in van loads, so the same matrix assign the Assets tab
  // offers is wanted here, where you can already see what is free.
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bookInOpen, setBookInOpen] = useState(false);
  const [bookInDate, setBookInDate] = useState(new Date().toISOString().slice(0, 10));
  const [bookInNote, setBookInNote] = useState('');
  const [bookingIn, setBookingIn] = useState(false);

  // The overview is a projection, not the asset itself, so fetch the full
  // record before opening the detail dialog on it.
  const openAsset = useCallback(async (assetId) => {
    try {
      const r = await axios.get(`${API}/inventory/assets/${assetId}`);
      setDetailAsset(r.data);
    } catch { toast.error(t('inv.overview.openFailed')); }
  }, [t]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/assignments/overview`);
      setData(r.data);
    } catch { toast.error(t('inv.overview.loadFailed')); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Holder filter options come from the data itself, so it only ever
  // lists crews and sites that actually hold something.
  const holders = useMemo(() => {
    const map = new Map();
    data.rows.forEach((row) => [...(row.current || []), ...(row.upcoming || []), ...(row.last ? [row.last] : [])]
      .forEach((a) => { if (a.target_id) map.set(a.target_id, { id: a.target_id, name: a.target_name || a.target_id, type: a.assignment_type }); }));
    return [...map.values()]
      .filter((h) => holderType === 'all' || h.type === holderType)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data.rows, holderType]);

  const filtered = useMemo(() => data.rows.filter((row) => {
    if (status !== 'all' && row.status !== status) return false;
    const holdings = [...(row.current || []), ...(row.last ? [row.last] : [])];
    if (holderType !== 'all' && !holdings.some((a) => a.assignment_type === holderType)) return false;
    if (holderId !== 'all' && !holdings.some((a) => a.target_id === holderId)) return false;
    if (weekFilter && ![...(row.current || []), ...(row.upcoming || [])].some((a) => overlapsWeek(weekFilter, a.date_from, a.date_to || a.date_from))) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [row.inventory_number, row.name, row.manufacturer, row.serial_number,
        row.category_name, holderLabel(row)].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [data.rows, status, holderType, holderId, search, weekFilter]);

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  // Select-all follows the filter, not the whole table: you filter down to the
  // free drills and tick the lot.
  const allFilteredPicked = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allFilteredPicked ? new Set() : new Set(filtered.map((r) => r.id)));

  // Kit comes back by the vanful; closing each line by hand is why the
  // system drifts out of date.
  const bookIn = async () => {
    setBookingIn(true);
    try {
      const r = await axios.post(`${API}/inventory/assets/batch-book-in`, {
        asset_ids: Array.from(selected),
        date: bookInDate,
        notes: bookInNote || null,
      });
      toast.success(t('inv.overview.bookInDone', {
        assets: r.data.assets_booked_in, count: r.data.assignments_closed,
      }));
      if (r.data.nothing_open?.length) {
        toast.info(t('inv.overview.nothingOpen', { list: r.data.nothing_open.join(', ') }));
      }
      setBookInOpen(false);
      setBookInNote('');
      setSelected(new Set());
      fetchOverview();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
    setBookingIn(false);
  };

  // A foreman walks the container with this: every tool booked to the
  // project, each with its QR code and a box to tick.
  const toolSheet = async () => {
    try {
      const lang = i18n.language?.startsWith('pl') ? 'pl' : 'en';
      const r = await axios.get(`${API}/qr/project/${holderId}/tool-sheet.pdf?lang=${lang}`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.overview.toolSheetFailed')); }
  };

  const navigate = (assignment) => {
    if (!navigateTo) return;
    const name = assignment.target_name || assignment.target_id;
    if (assignment.assignment_type === 'project') navigateTo('projects', { searchProject: name });
    else navigateTo('employees', { searchEmployee: name });
  };

  const clearFilters = () => { setWeekFilter(null); setSearch(''); setStatus('all'); setHolderType('all'); setHolderId('all'); };
  const hasFilters = search || status !== 'all' || holderType !== 'all' || holderId !== 'all';

  const summary = [
    { key: 'all', icon: Package, label: t('inv.tabs.assets'), value: data.totals?.assets ?? 0, cls: 'text-slate-700' },
    { key: 'assigned', icon: FolderOpen, label: statusLabel('assigned'), value: data.totals?.assigned ?? 0, cls: 'text-emerald-700' },
    { key: 'overdue', icon: AlertTriangle, label: statusLabel('overdue'), value: data.totals?.overdue ?? 0, cls: 'text-amber-700' },
    { key: 'unassigned', icon: Package, label: statusLabel('unassigned'), value: data.totals?.unassigned ?? 0, cls: 'text-slate-500' },
  ];

  return (
    <div className="space-y-4" data-testid="assignments-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {summary.map((s) => (
          <button key={s.key} onClick={() => setStatus(s.key === 'all' ? 'all' : s.key)}
            className={`text-left rounded-lg border bg-white p-3 transition-colors hover:border-slate-300
              ${status === s.key || (s.key === 'all' && status === 'all') ? 'border-slate-400' : 'border-slate-200'}`}
            data-testid={`assignments-kpi-${s.key}`}>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <s.icon className={`w-4 h-4 ${s.cls}`} />{s.label}
            </div>
            <div className={`text-2xl font-semibold ${s.cls}`}>{s.value}</div>
          </button>
        ))}
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9"
                placeholder={t('inv.overview.searchPlaceholder')} data-testid="assignments-search-input" />
            </div>
            <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="assignments-week-filter" />
            <div>
              <Label className="sr-only">{t('common.status')}</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9" data-testid="assignments-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('inv.overview.anyStatus')}</SelectItem>
                  <SelectItem value="assigned">{statusLabel('assigned')}</SelectItem>
                  <SelectItem value="overdue">{statusLabel('overdue')}</SelectItem>
                  <SelectItem value="unassigned">{statusLabel('unassigned')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={holderType} onValueChange={(v) => { setHolderType(v); setHolderId('all'); }}>
                <SelectTrigger className="h-9" data-testid="assignments-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('inv.overview.crewsAndSites')}</SelectItem>
                  <SelectItem value="project">{t('inv.overview.sites')}</SelectItem>
                  <SelectItem value="employee">{t('inv.overview.crew')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Select value={holderId} onValueChange={setHolderId}>
                <SelectTrigger className="h-9" data-testid="assignments-holder-filter"><SelectValue placeholder={t('inv.overview.anyone')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('inv.overview.anyone')}</SelectItem>
                  {holders.map((h) => (<SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {hasFilters && (
                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={clearFilters}
                  title={t('inv.overview.clearFilters')} data-testid="assignments-clear-filters">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {holderType === 'project' && holderId !== 'all' && (
            <Button variant="outline" size="sm" className="self-start" onClick={toolSheet}
              data-testid="project-tool-sheet-btn">
              <QrCode className="mr-1 h-4 w-4" /> {t('inv.overview.toolSheet')}
            </Button>
          )}

          {isAdmin && selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
              data-testid="assignments-batch-bar">
              <span className="text-sm font-medium">{t('inv.assets.selectedCount', { count: selected.size })}</span>
              <Button variant="outline" size="sm" onClick={() => setBulkAssignOpen(true)}
                data-testid="assignments-batch-assign-btn">
                <UserCheck className="w-4 h-4 mr-1" /> {t('inv.assets.assign')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setBookInOpen(true)}
                data-testid="assignments-book-in-btn">
                <Undo2 className="w-4 h-4 mr-1" /> {t('inv.overview.bookIn')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}
                data-testid="assignments-clear-selection-btn">
                {t('common.clear')}
              </Button>
            </div>
          )}

          <p className="text-xs text-slate-500" data-testid="assignments-result-count">
            {loading ? t('common.loading') : t('inv.overview.countLine', { shown: filtered.length, total: data.rows.length, date: data.as_of })}
          </p>

          <Table data-testid="assignments-table">
            <TableHeader>
              <TableRow>
                {isAdmin && (
                  <TableHead className="w-[40px]">
                    <button onClick={toggleAll} className="flex items-center"
                      data-testid="assignments-select-all">
                      {allFilteredPicked ? <CheckSquare className="w-4 h-4 text-blue-600" />
                        : <Square className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  </TableHead>
                )}
                <TableHead>{t('inv.overview.asset')}</TableHead>
                <TableHead>{t('inv.overview.currentlyWith')}</TableHead>
                <TableHead className="hidden sm:table-cell w-[160px]">{t('common.status')}</TableHead>
                <TableHead className="hidden lg:table-cell w-[110px]">{t('inv.overview.since')}</TableHead>
                <TableHead className="hidden lg:table-cell w-[110px]">{t('inv.common.until')}</TableHead>
                <TableHead className="hidden xl:table-cell">{t('common.notes')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => {
                const first = (row.current || [])[0] || row.last;
                return (
                  <TableRow key={row.id} className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openAsset(row.id)}
                    data-testid={`assignment-overview-row-${row.inventory_number}`}>
                    {isAdmin && (
                      <TableCell className="align-top">
                        <button role="checkbox" aria-checked={selected.has(row.id)}
                          aria-label={t('inv.assets.selectAria', { code: row.inventory_number })}
                          onClick={(e) => { e.stopPropagation(); toggleSelect(row.id); }}
                          data-testid={`assignments-select-${row.inventory_number}`}>
                          {selected.has(row.id) ? <CheckSquare className="w-4 h-4 text-blue-600" />
                            : <Square className="w-4 h-4 text-muted-foreground" />}
                        </button>
                      </TableCell>
                    )}
                    <TableCell className="align-top break-words">
                      <span className="font-mono text-sm font-medium inline-flex items-center gap-1">
                        {row.inventory_number}
                        <CopyIconButton label={t('inv.assets.inventoryNumber')} value={row.inventory_number}
                          testId={`copy-overview-number-${row.inventory_number}`} />
                      </span>
                      <div className="text-sm break-words">{row.name}</div>
                      <div className="text-xs text-slate-500 break-words">
                        {[row.category_name, row.manufacturer].filter(Boolean).join(' · ')}
                      </div>
                      <div className="sm:hidden mt-1">
                        <Badge className={`text-xs ${STATUS_CLS[row.status]}`}>{statusLabel(row.status)}</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="align-top break-words">
                      {(row.current || []).length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {row.current.map((a) => <Holder key={a.id} assignment={a} onNavigate={navigateTo && navigate} />)}
                        </div>
                      ) : row.last ? (
                        <div className="text-slate-500">
                          <Holder assignment={row.last} onNavigate={navigateTo && navigate} />
                          <span className="block text-xs">{t('inv.overview.notBookedBackShort')}</span>
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                      {(row.upcoming || []).length > 0 && (
                        <span className="block text-xs text-slate-500 mt-1">
                          {t('inv.overview.nextUp', { name: row.upcoming[0].target_name, date: fmtDateWeek(row.upcoming[0].date_from) })}
                        </span>
                      )}
                      <span className="lg:hidden block text-xs text-slate-500">
                        {first?.date_from ? t('inv.overview.sinceDate', { date: first.date_from }) : ''}
                        {first?.ongoing ? ` · ${t('inv.common.ongoing')}` : first?.date_to ? ` · ${t('inv.overview.untilDate', { date: first.date_to })}` : ''}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell align-top">
                      <Badge className={`text-xs whitespace-nowrap ${STATUS_CLS[row.status]}`}>{statusLabel(row.status)}</Badge>
                      {row.days_overdue > 0 && (
                        <span className="block text-xs text-amber-700 mt-1">{row.days_overdue} days</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell align-top text-sm">{first?.date_from || '—'}</TableCell>
                    <TableCell className="hidden lg:table-cell align-top text-sm">
                      {first?.ongoing ? <Badge variant="secondary" className="text-xs">{t('inv.common.ongoing')}</Badge> : (first?.date_to || '—')}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell align-top text-sm text-slate-500 break-words">{first?.notes || '—'}</TableCell>
                  </TableRow>
                );
              })}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-slate-500 py-8" data-testid="assignments-empty">
                    {data.rows.length === 0 ? t('inv.overview.emptyAll') : t('inv.overview.emptyFiltered')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <FloatingActions actions={[
        { icon: RefreshCw, label: t('common.refresh'), testId: 'assignments-floating-refresh-btn', onClick: fetchOverview },
        { icon: FileSpreadsheet, label: t('inv.assets.exportView'), testId: 'assignments-floating-export-btn',
          onClick: () => exportView({
            filename: 'asset-assignments', sheetName: 'Assignments', rows: filtered,
            columns: [
              { header: t('inv.assets.invNo'), key: 'inventory_number' },
              { header: t('inv.overview.asset'), key: 'name' },
              { header: t('inv.common.category'), key: 'category_name' },
              { header: t('common.status'), value: (r) => statusLabel(r.status) },
              { header: t('inv.overview.currentlyWith'), value: (r) => holderLabel(r) },
              { header: t('inv.overview.holderType'), value: (r) => ((r.current || [])[0] || r.last || {}).assignment_type || '' },
              { header: t('inv.overview.since'), value: (r) => ((r.current || [])[0] || r.last || {}).date_from || '' },
              { header: t('inv.common.until'), value: (r) => { const a = (r.current || [])[0] || r.last || {}; return a.ongoing ? t('inv.common.ongoing') : (a.date_to || ''); } },
              { header: t('inv.overview.daysOverdue'), value: (r) => r.days_overdue || '' },
              { header: t('common.notes'), value: (r) => ((r.current || [])[0] || r.last || {}).notes || '' },
            ],
          }) },
      ]} />

      {bulkAssignOpen && (
        <MatrixAssignDialog
          open={bulkAssignOpen}
          onOpenChange={setBulkAssignOpen}
          assetIds={Array.from(selected)}
          projects={projects}
          employees={employees}
          onDone={() => { setSelected(new Set()); fetchOverview(); }}
        />
      )}

      <Dialog open={bookInOpen} onOpenChange={setBookInOpen}>
        <DialogContent className="max-w-md" data-testid="book-in-dialog">
          <DialogHeader>
            <DialogTitle>{t('inv.overview.bookInTitle', { count: selected.size })}</DialogTitle>
            <DialogDescription>{t('inv.overview.bookInHint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t('inv.overview.bookInDate')}</Label>
              <Input type="date" value={bookInDate} onChange={(e) => setBookInDate(e.target.value)}
                data-testid="book-in-date" />
            </div>
            <div>
              <Label className="text-xs">{t('inv.overview.bookInNote')}</Label>
              <Input value={bookInNote} onChange={(e) => setBookInNote(e.target.value)}
                placeholder={t('inv.overview.bookInNotePlaceholder')} data-testid="book-in-note" />
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setBookInOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={bookIn} disabled={bookingIn || !bookInDate} data-testid="book-in-confirm-btn">
              <Undo2 className="w-4 h-4 mr-1" /> {t('inv.overview.bookIn')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {detailAsset && (
        <AssetDetail
          asset={detailAsset}
          open={!!detailAsset}
          onClose={() => setDetailAsset(null)}
          onEdit={() => setDetailAsset(null)}
          onDeleted={() => { setDetailAsset(null); fetchOverview(); }}
          onUpdated={(updated) => { setDetailAsset(updated); fetchOverview(); }}
          categories={categories}
          conditions={conditions}
          suppliers={suppliers}
          employees={employees}
          projects={projects}
          isAdmin={isAdmin}
          config={config}
          navigateTo={navigateTo}
        />
      )}
    </div>
  );
};

export default AssignmentsOverview;
